import { useState, useCallback, useEffect, useRef } from 'react'
import { PanelLeft, Plus, Pencil, Library } from 'lucide-react'
import { ContentCpEditor } from './ContentCpEditor'
import { ContentCpCardsPanel } from './ContentCpCardsPanel'
import { ContentCpSessionSidebar } from './ContentCpSessionSidebar'
import { ContentCpRenameSessionModal } from './ContentCpRenameSessionModal'
import { ContentCpMobileNav } from './ContentCpMobileNav'
import { ContentCpStatusBar } from './ContentCpStatusBar'
import { ResizableDivider } from '@/features/develop/components/ResizableDivider'
import { useContentCpStore } from '@/stores/useContentCpStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'

const MIN_PANEL_WIDTH = 300
const MAX_PANEL_WIDTH = 600
const DEFAULT_PANEL_WIDTH = 400
const DEFAULT_PANEL_WIDTH_WITH_SIDEBAR = 350
const SIDEBAR_WIDTH = 260

const CONTENT_CP_USER_ID_KEY = 'content-cp-store-user-id'

export function ContentCpView() {
  const {
    sessionName,
    mobileView,
    showSessionSidebar,
    showCardsPanel,
    toggleSessionSidebar,
    closeSessionSidebar,
    toggleCardsPanel,
    createNewSession,
    loadSessionFromDb,
    renameSession,
    setMobileView,
    startNewSession,
    loadSessions,
  } = useContentCpStore()

  const isMobile = useIsMobile()
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [isVerifyingUser, setIsVerifyingUser] = useState(true)

  const { initialized: authInitialized, session: authSession, initialize: initializeAuth } = useAuthStore()

  const hasInitialized = useRef(false)

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  // User verification
  useEffect(() => {
    if (!authInitialized) return
    if (!authSession?.user?.id) {
      setIsVerifyingUser(false)
      return
    }

    const currentUserId = authSession.user.id
    const storedUserId = localStorage.getItem(CONTENT_CP_USER_ID_KEY)

    console.log('[ContentCpView] Verifying user. Current:', currentUserId, 'Stored:', storedUserId)

    if (storedUserId && storedUserId !== currentUserId) {
      console.log('[ContentCpView] User mismatch! Clearing stale data before render')
      localStorage.setItem(CONTENT_CP_USER_ID_KEY, currentUserId)
      localStorage.removeItem('content-cp-store')

      const persistApi = (useContentCpStore as unknown as { persist?: { clearStorage: () => void } }).persist
      if (persistApi?.clearStorage) {
        persistApi.clearStorage()
      }

      startNewSession()

      const rehydrate = (useContentCpStore as unknown as { persist?: { rehydrate: () => Promise<void> } }).persist?.rehydrate
      if (rehydrate) {
        rehydrate()
      }

      hasInitialized.current = false
    } else if (!storedUserId) {
      localStorage.setItem(CONTENT_CP_USER_ID_KEY, currentUserId)
    }

    setIsVerifyingUser(false)
  }, [authInitialized, authSession?.user?.id, startNewSession])

  // Session initialization
  useEffect(() => {
    if (hasInitialized.current) return
    if (!authInitialized) return
    if (isVerifyingUser) return

    if (!authSession) {
      console.log('[ContentCpView] No auth session, redirecting to Z login')
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      return
    }

    const checkAndInit = async () => {
      await new Promise(resolve => setTimeout(resolve, 50))

      const state = useContentCpStore.getState()
      const hydratedSessionId = state.sessionId

      if (hasInitialized.current) return
      hasInitialized.current = true

      // Load sessions list
      loadSessions()

      if (hydratedSessionId) {
        console.log('[ContentCpView] Loading session from DB:', hydratedSessionId)
        loadSessionFromDb(hydratedSessionId)
      } else {
        console.log('[ContentCpView] No session found, creating new')
        createNewSession()
      }
    }

    checkAndInit()
  }, [authInitialized, authSession, isVerifyingUser, loadSessions, loadSessionFromDb, createNewSession])

  const [cardsWidth, setCardsWidth] = useState(DEFAULT_PANEL_WIDTH)

  useEffect(() => {
    setCardsWidth(showSessionSidebar ? DEFAULT_PANEL_WIDTH_WITH_SIDEBAR : DEFAULT_PANEL_WIDTH)
  }, [showSessionSidebar])

  const handleResize = useCallback((delta: number) => {
    setCardsWidth(prev => {
      const newWidth = prev - delta
      return Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, newWidth))
    })
  }, [])

  const handleMobileSessionSelect = useCallback(() => {
    setMobileView('insert')
  }, [setMobileView])

  // Loading state
  if (isVerifyingUser || !authInitialized) {
    return (
      <div className="h-dvh flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    )
  }

  // Mobile layout
  if (isMobile) {
    return (
      <div className="h-dvh flex flex-col bg-background overflow-hidden">
        {/* Mobile Header */}
        <header className="flex-shrink-0 h-14 border-b border-border">
          <div className="px-4 h-full flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <h1 className="text-sm font-medium text-foreground truncate">{sessionName}</h1>
              <button
                onClick={() => setShowRenameModal(true)}
                className="p-1.5 text-muted-foreground hover:text-foreground rounded transition-colors flex-shrink-0"
                title="Rename session"
              >
                <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>

            <button
              onClick={() => createNewSession()}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
              title="New session"
            >
              <Plus className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </header>

        {/* Main content area - extra padding at bottom for status bar */}
        <main className="flex-1 overflow-hidden pb-14 pb-mobile-nav">
          {mobileView === 'sessions' && (
            <div className="h-full">
              <ContentCpSessionSidebar
                onClose={() => setMobileView('insert')}
                onSessionSelect={handleMobileSessionSelect}
              />
            </div>
          )}
          {mobileView === 'insert' && (
            <div className="h-full">
              <ContentCpEditor />
            </div>
          )}
          {mobileView === 'cards' && (
            <div className="h-full">
              <ContentCpCardsPanel />
            </div>
          )}
        </main>

        <ContentCpMobileNav />
        <ContentCpStatusBar />

        <ContentCpRenameSessionModal
          isOpen={showRenameModal}
          currentName={sessionName}
          onClose={() => setShowRenameModal(false)}
          onSubmit={(name) => {
            renameSession(name)
            setShowRenameModal(false)
          }}
        />
      </div>
    )
  }

  // Desktop layout
  return (
    <div className="h-dvh flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 h-14">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-full flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button
              onClick={toggleSessionSidebar}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
              title={showSessionSidebar ? "Hide sessions" : "Show sessions"}
            >
              <PanelLeft className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              onClick={() => createNewSession()}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
              title="New session"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={toggleCardsPanel}
              className={cn(
                "p-1.5 rounded transition-colors",
                showCardsPanel
                  ? "text-foreground bg-muted"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
              title={showCardsPanel ? "Hide cards" : "Show cards"}
            >
              <Library className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Main content area - extra padding at bottom for status bar */}
      <main className="flex-1 overflow-hidden pb-8">
        <div className="h-full max-w-7xl mx-auto px-6 md:px-8 flex">
          {/* Session Sidebar */}
          {showSessionSidebar && (
            <div
              className="flex-shrink-0 h-full border-r border-border"
              style={{ width: SIDEBAR_WIDTH }}
            >
              <ContentCpSessionSidebar onClose={closeSessionSidebar} />
            </div>
          )}

          {/* Main content column */}
          <div className={cn("flex-1 flex flex-col min-w-0", showSessionSidebar && "pl-4")}>
            {/* Session title */}
            <div className="flex-shrink-0 pt-3 pb-2 flex items-center gap-2 group">
              <h1 className="text-sm font-medium text-foreground">{sessionName}</h1>
              <button
                onClick={() => setShowRenameModal(true)}
                className="p-1 text-muted-foreground/0 group-hover:text-muted-foreground hover:!text-foreground rounded transition-colors"
                title="Rename session"
              >
                <Pencil className="w-3 h-3" strokeWidth={1.5} />
              </button>
            </div>

            {/* Editor pane */}
            <div className="flex-1 overflow-hidden">
              <ContentCpEditor />
            </div>
          </div>

          {/* Cards panel */}
          {showCardsPanel && (
            <>
              <ResizableDivider onResize={handleResize} />
              <div
                className="flex-shrink-0 overflow-hidden"
                style={{ width: cardsWidth }}
              >
                <ContentCpCardsPanel />
              </div>
            </>
          )}
        </div>
      </main>

      {/* Status bar */}
      <ContentCpStatusBar />

      {/* Rename Session Modal */}
      <ContentCpRenameSessionModal
        isOpen={showRenameModal}
        currentName={sessionName}
        onClose={() => setShowRenameModal(false)}
        onSubmit={(name) => {
          renameSession(name)
          setShowRenameModal(false)
        }}
      />
    </div>
  )
}
